import { Close, Notifications } from "@mui/icons-material";
import {
  Badge,
  Box,
  ClickAwayListener,
  Chip,
  Fade,
  IconButton,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useNotification from "../../hooks/useNotification";
import { useReadNotificationMutation } from "../../redux/api/notificationApi";
import { useNavigate } from "react-router-dom";

export default function Notification() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const notification = useNotification();
  const [readNotificationMutation] = useReadNotificationMutation();

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  useEffect(() => {
    setNotifications([
      ...notification.filter((item) => item.status === 0),
      ...notification.filter((item) => item.status !== 0),
    ]);
  }, [notification]);

  const open = Boolean(anchorEl);
  const unreadCount = notifications.filter((item) => item.status === 0).length;

  const handleClose = () => setAnchorEl(null);

  const handleClickNotification = (notification) => {
    const { notificationId: id, status } = notification;
    if (status === 0) {
      readNotificationMutation(id)
        .unwrap()
        .then(() => handleNavigateToNotification(notification))
        .catch(console.error);
    } else {
      handleNavigateToNotification(notification);
    }
  };

  const handleNavigateToNotification = (notification) => {
    setAnchorEl(null);
    const routes = {
      prop: `/properties/?propertyId=${notification.id}`,
      task: `/tasks?taskId=${notification.id}`,
      "ag-s": `/users/${notification.id}`,
      "at-s": `/roles/${notification.id}`,
      agsu: `/users/${notification.id}`,
      agtp: `/roles/${notification.id}`,
      lead: `/allLeads?leadId=${notification.id}`,
    };
    if (routes[notification.tag]) navigate(routes[notification.tag], { replace: true });
  };

  const getNotificationIcon = (tag) => {
    const icons = {
      prop: { color: "#3B82F6" },
      task: { color: "#8B5CF6" },
      lead: { color: "#F59E0B" },
      "ag-s": { color: "#10B981" },
      "at-s": { color: "#EF4444" },
      agsu: { color: "#8B5CF6" },
      agtp: { color: "#06B6D4" },
    };
    return icons[tag] || { color: "#6B7280" };
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return `${Math.floor(diffInHours * 60)}m ago`;
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  return (
    <>
      <IconButton
        color="primary"
        onClick={handleClick}
        sx={{
          position: "relative",
          transition: "all 0.3s",
          "&:hover": {
            transform: "scale(1.1)",
            backgroundColor: "rgba(86, 198, 98, 0.12)",
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.65rem",
              fontWeight: "bold",
              height: 18,
              minWidth: 18,
              boxShadow: "0 0 0 2px #fff",
              background: "linear-gradient(135deg, #FF6B6B, #EE5A24)",
            },
          }}
        >
          <Notifications
            sx={{
              fontSize: 26,
              color: open ? "#56C662" : "text.secondary",
              transition: "color 0.3s ease",
            }}
          />
        </Badge>
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: 9999 }}
        transition
        modifiers={[
          { name: "offset", options: { offset: [0, 12] } },
          { name: "preventOverflow", options: { mainAxis: true, altAxis: true } },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={250}>
            <Paper
              elevation={16}
              sx={{
                width: { xs: "90vw", sm: 380, md: 400 },
                maxHeight: { xs: "70vh", sm: 560 },
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.1)",
                background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                backdropFilter: "blur(20px)",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* Header */}
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      background: "linear-gradient(135deg, #56C662 0%, #4CAF50 100%)",
                      color: "white",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        fontSize={{ xs: "1rem", sm: "1.1rem" }}
                      >
                        Notifications
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.9, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                      >
                        {unreadCount > 0
                          ? `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}`
                          : "All caught up!"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {unreadCount > 0 && (
                        <Chip
                          label={`${unreadCount} New`}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.2)",
                            color: "white",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            height: 24,
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{
                          color: "white",
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Notifications List */}
                  <Box
                    sx={{
                      maxHeight: { xs: "60vh", sm: 480 },
                      overflowY: "auto",
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#E2E8F0",
                        borderRadius: 3,
                      },
                    }}
                  >
                    <Stack spacing={1} sx={{ p: { xs: 1, sm: 1.5 } }}>
                      {notifications.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                          <Notifications sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
                          <Typography variant="body2" fontWeight={500}>
                            No notifications
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            You're all caught up!
                          </Typography>
                        </Box>
                      ) : (
                        notifications.map((notif) => {
                          const isUnread = notif.status === 0;
                          const iconConfig = getNotificationIcon(notif.tag);

                          return (
                            <Paper
                              key={notif.notificationId}
                              onClick={() => handleClickNotification(notif)}
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                display: "flex",
                                gap: 2,
                                alignItems: "flex-start",
                                borderRadius: 2,
                                cursor: "pointer",
                                background: isUnread
                                  ? "linear-gradient(135deg, rgba(86,198,98,0.08) 0%, rgba(86,198,98,0.02) 100%)"
                                  : "#FFFFFF",
                                border: isUnread
                                  ? "1.5px solid rgba(86,198,98,0.2)"
                                  : "1px solid rgba(0,0,0,0.04)",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                                },
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isUnread ? 600 : 500,
                                    color: isUnread ? "text.primary" : "text.secondary",
                                    lineHeight: 1.4,
                                    mb: 0.5,
                                  }}
                                >
                                  {notif.SenderName && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontWeight: 700,
                                        color: "#56C662",
                                        mr: 0.5,
                                      }}
                                    >
                                      {notif.SenderName}
                                    </Typography>
                                  )}
                                  {notif.description}
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mt: 0.5,
                                  }}
                                >
                                  <Chip
                                    label={notif.tag?.trim() || "General"}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: "0.65rem",
                                      height: 20,
                                      fontWeight: 600,
                                      borderColor: `${iconConfig.color}40`,
                                      color: iconConfig.color,
                                      backgroundColor: `${iconConfig.color}10`,
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatTime(notif.sendDate)}
                                  </Typography>
                                </Box>
                              </Box>

                              {isUnread && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: "#56C662",
                                    flexShrink: 0,
                                    mt: 0.5,
                                  }}
                                />
                              )}
                            </Paper>
                          );
                        })
                      )}
                    </Stack>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
}
